import { useState, useEffect } from "react";
import { Modal, Button, Descriptions, Timeline } from "antd";
import { WifiIcon, WifiOffIcon } from "./Icons";

const getNetworkConnection = () => {
  return navigator.connection || navigator.mozConnection || navigator.webkitConnection || null;
};

const getNetworkConnectionInfo = () => {
  const connection = getNetworkConnection();
  if (!connection) {
    return {};
  }
  return {
    rtt: connection.rtt,
    type: connection.type,
    saveData: connection.saveData,
    downlink: connection.downlink,
    downlinkMax: connection.downlinkMax,
    effectiveType: connection.effectiveType,
  };
};

export default function NetworkStatusDetector() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [networkInfo, setNetworkInfo] = useState(getNetworkConnectionInfo());
  const [statusHistory, setStatusHistory] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setNetworkInfo(getNetworkConnectionInfo());
      setStatusHistory((prev) => [
        ...prev,
        {
          status: "online",
          timestamp: new Date().toLocaleTimeString(),
          info: getNetworkConnectionInfo(),
        },
      ]);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setStatusHistory((prev) => [
        ...prev,
        {
          status: "offline",
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
    };

    const handleConnectionChange = () => {
      setNetworkInfo(getNetworkConnectionInfo());
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    const connection = getNetworkConnection();
    if (connection) {
      connection.addEventListener("change", handleConnectionChange);
    }

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      if (connection) {
        connection.removeEventListener("change", handleConnectionChange);
      }
    };
  }, []);

  const networkItems = [
    networkInfo.effectiveType && {
      key: "effectiveType",
      label: "Tipo de Conexão",
      children: networkInfo.effectiveType.toUpperCase(),
    },
    networkInfo.rtt !== undefined && {
      key: "rtt",
      label: "Latência (RTT)",
      children: `${networkInfo.rtt} ms`,
    },
    networkInfo.downlink !== undefined && {
      key: "downlink",
      label: "Velocidade Download",
      children: `${networkInfo.downlink} Mbps`,
    },
    networkInfo.downlinkMax !== undefined && {
      key: "downlinkMax",
      label: "Velocidade Máxima",
      children: `${networkInfo.downlinkMax} Mbps`,
    },
    networkInfo.type && {
      key: "type",
      label: "Tipo",
      children: networkInfo.type,
    },
    networkInfo.saveData !== undefined && {
      key: "saveData",
      label: "Modo Economia",
      children: networkInfo.saveData ? "Ativo" : "Inativo",
    },
  ].filter(Boolean);

  const timelineItems = statusHistory
    .slice()
    .reverse()
    .map((entry, index) => ({
      key: index,
      color: entry.status === "online" ? "green" : "red",
      children: (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: 500, color: entry.status === "online" ? "#52c41a" : "#ff4d4f" }}>
            {entry.status === "online" ? "Ficou Online" : "Ficou Offline"}
          </span>
          <span style={{ fontSize: "14px", color: "#8c8c8c" }}>{entry.timestamp}</span>
        </div>
      ),
    }));

  return (
    <>
      <div onClick={() => setModalOpen(true)} style={{ cursor: "pointer", display: "inline-block", padding: "8px", paddingLeft: "20px", paddingRight: "20px" }}>
        {isOnline ? <WifiIcon size={24} style={{ color: "#52c41a" }} /> : <WifiOffIcon size={24} style={{ color: "#ff4d4f" }} />}
      </div>

      <Modal
        width={700}
        title="Status da Conexão"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setModalOpen(false)}>
            Fechar
          </Button>,
        ]}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          {/* Current Status */}
          <div
            style={{
              padding: "16px",
              borderRadius: "8px",
              backgroundColor: isOnline ? "#f6ffed" : "#fff1f0",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              {isOnline ? <WifiIcon size={32} style={{ color: "#52c41a" }} /> : <WifiOffIcon size={32} style={{ color: "#ff4d4f" }} />}
              <div>
                <h3 style={{ fontSize: "20px", fontWeight: 600, color: isOnline ? "#52c41a" : "#ff4d4f", margin: 0 }}>{isOnline ? "Online" : "Offline"}</h3>
                <p style={{ color: isOnline ? "#52c41a" : "#ff4d4f", margin: 0 }}>{isOnline ? "Conexão ativa" : "Sem conexão com a internet"}</p>
              </div>
            </div>
          </div>

          {/* Network Information */}
          {isOnline && networkItems.length > 0 && (
            <div>
              <h4 style={{ fontSize: "16px", fontWeight: 600, marginBottom: "12px" }}>Informações da Rede</h4>
              <Descriptions bordered column={2} items={networkItems} />
            </div>
          )}

          {/* History */}
          <div>
            <h4 style={{ fontSize: "16px", fontWeight: 600, marginBottom: "12px" }}>Histórico de Mudanças</h4>
            {statusHistory.length === 0 ? (
              <p style={{ color: "#8c8c8c", textAlign: "center", padding: "6px 0" }}>Nenhuma mudança detectada</p>
            ) : (
              <div style={{ overflowY: "auto" }}>
                <Timeline items={timelineItems} />
              </div>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}
